/**
 * WebsiteWizard — /websites/new (also invoked via ?source_type=...&source_id=...)
 *
 * Simple three-step create flow:
 *   1. What are we building? (business / institution / sports_club / blank)
 *   2. Pick a template + theme + layout
 *   3. Review & publish
 *
 * If the URL contains `source_type` + `source_id`, we skip step 1 and
 * pre-fill from the source entity.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Sparkles, ChevronRight, ChevronLeft, Save, Palette, Layout,
  Building2, GraduationCap, Trophy, Feather, Globe, Landmark, Utensils,
} from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const SOURCE_ICONS = { business: Building2, food_business: Utensils, institution: GraduationCap, sports_club: Trophy, government_department: Landmark, blank: Feather };

export default function WebsiteWizard() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();

  const preSourceType = params.get("source_type");
  const preSourceId   = params.get("source_id");
  const preTemplate   = params.get("template");

  const [step, setStep] = useState(preSourceType && preSourceId ? 2 : 1);
  const [catalogue, setCatalogue] = useState(null);
  const [sourceType, setSourceType] = useState(preSourceType || "");
  const [sourceId,   setSourceId]   = useState(preSourceId || "");
  const [blankName,  setBlankName]  = useState("");
  const [template, setTemplate] = useState(preTemplate || "corporate");
  const [theme,    setTheme]    = useState("orange");
  const [layout,   setLayout]   = useState("one_page");
  const [saving,   setSaving]   = useState(false);

  useEffect(() => {
    http.get("/websites/templates").then(r => setCatalogue(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!authLoading && !user) {
      toast.error("Sign in to create a website.");
      nav(`/login?redirect=${encodeURIComponent("/websites/new")}`, { replace: true });
    }
  }, [authLoading, user, nav]);

  // Smart defaults per source_type — but honour ?template=... if present.
  useEffect(() => {
    if (preTemplate) return; // caller already picked
    if (sourceType === "institution") setTemplate("educational");
    else if (sourceType === "sports_club") setTemplate("sports_club");
    else if (sourceType === "food_business") { setTemplate("restaurant"); setTheme("cream"); }
    else if (sourceType === "business") setTemplate("corporate");
    else if (sourceType === "government_department") { setTemplate("corporate"); setTheme("blue"); }
  }, [sourceType, preTemplate]);

  const canContinueStep1 =
    (sourceType && sourceType !== "blank" && sourceId.trim()) ||
    (sourceType === "blank" && blankName.trim());

  const submit = async () => {
    setSaving(true);
    try {
      let r;
      if (sourceType === "blank") {
        r = await http.post("/websites", { name: blankName.trim(), template, theme, layout });
      } else {
        r = await http.post("/websites/from-source", {
          source_type: sourceType, source_id: sourceId.trim(),
          template, theme, layout,
        });
      }
      toast.success("Draft website created!");
      nav(`/websites/${r.data.site.id}/edit`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create website.");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="website-wizard">
      <div className="overline mb-1">INSTANT WEBSITE</div>
      <h1 className="font-display text-4xl mb-2 inline-flex items-center gap-2">
        <Sparkles className="w-8 h-8" />Build your website in minutes
      </h1>
      <p className="opacity-70 mb-6">
        Pick a template, tweak the colours, publish. Your site lives at
        <code className="mx-1 px-1.5 py-0.5 rounded text-xs" style={{ background: "var(--wofy-surface)" }}>
          /sites/&lt;your-name&gt;
        </code>
        and syncs with your Social Pulse profile automatically.
      </p>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-6 text-xs opacity-70" data-testid="wizard-stepper">
        {[1, 2, 3].map(n => (
          <div key={n} className={`inline-flex items-center gap-1 ${step >= n ? "opacity-100" : ""}`}>
            <span className={`w-6 h-6 rounded-full grid place-items-center text-white ${step >= n ? "" : "opacity-30"}`}
                  style={{ background: "var(--wofy-primary)" }}>{n}</span>
            {n < 3 && <ChevronRight className="w-3 h-3" />}
          </div>
        ))}
        <span className="ml-2 font-mono">
          {step === 1 && "Step 1 — Who is this for?"}
          {step === 2 && "Step 2 — Design"}
          {step === 3 && "Step 3 — Review"}
        </span>
      </div>

      {step === 1 && (
        <section className="space-y-4" data-testid="step-source">
          <div className="grid gap-3 sm:grid-cols-2">
            {["business", "food_business", "institution", "sports_club", "government_department", "blank"].map(t => {
              const Icon = SOURCE_ICONS[t];
              const active = sourceType === t;
              const label =
                t === "sports_club" ? "Sports Club" :
                t === "food_business" ? "Food Business" :
                t === "government_department" ? "Government Department" :
                t === "blank" ? "Blank site" :
                t[0].toUpperCase() + t.slice(1);
              return (
                <button key={t} type="button" onClick={() => setSourceType(t)}
                  className={`wofy-card !p-4 text-left transition ${active ? "ring-2 ring-black" : ""}`}
                  data-testid={`source-${t}`}>
                  <Icon className="w-6 h-6 mb-2" />
                  <div className="font-medium">{label}</div>
                  <div className="text-xs opacity-70 mt-0.5">
                    {t === "blank" ? "Start from a blank canvas."
                     : `Auto-fill from your Social Pulse ${label.toLowerCase()} profile.`}
                  </div>
                </button>
              );
            })}
          </div>

          {sourceType && sourceType !== "blank" && (
            <div>
              <label className="block text-[11px] font-mono uppercase tracking-wider opacity-70 mb-1">
                Source ID *
              </label>
              <input value={sourceId} onChange={e => setSourceId(e.target.value)}
                placeholder={`Paste the ${sourceType.replace("_", " ")} ID from Social Pulse`}
                className="wofy-input font-mono text-xs" data-testid="source-id-input" />
              <p className="text-xs opacity-60 mt-1">
                You can find the ID on your {sourceType.replace("_", " ")} profile page (URL slug works too, but the
                exact ID is safest).
              </p>
            </div>
          )}

          {sourceType === "blank" && (
            <div>
              <label className="block text-[11px] font-mono uppercase tracking-wider opacity-70 mb-1">
                Site name *
              </label>
              <input value={blankName} onChange={e => setBlankName(e.target.value)}
                placeholder="e.g. Green Valley Consulting"
                className="wofy-input" data-testid="blank-name-input" />
            </div>
          )}

          <div className="pt-2 flex justify-end">
            <button disabled={!canContinueStep1} onClick={() => setStep(2)}
              className="wofy-btn-primary inline-flex items-center gap-1.5 disabled:opacity-40"
              data-testid="next-step-1">
              Continue <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      )}

      {step === 2 && (
        <section className="space-y-6" data-testid="step-design">
          <div>
            <h2 className="font-display text-xl mb-2 inline-flex items-center gap-2"><Layout className="w-5 h-5" />Template</h2>
            <div className="grid gap-2 sm:grid-cols-2">
              {(catalogue?.templates || []).map(t => (
                <button key={t.slug} type="button" onClick={() => setTemplate(t.slug)}
                  className={`wofy-card !p-3 text-left transition ${template === t.slug ? "ring-2 ring-black" : ""}`}
                  data-testid={`template-${t.slug}`}>
                  <div className="font-medium">{t.label}</div>
                  <div className="text-xs opacity-70 mt-0.5">{t.hint}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-display text-xl mb-2 inline-flex items-center gap-2"><Palette className="w-5 h-5" />Colour theme</h2>
            <div className="flex flex-wrap gap-2">
              {(catalogue?.themes || []).map(t => (
                <button key={t.slug} type="button" onClick={() => setTheme(t.slug)}
                  className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border ${theme === t.slug ? "ring-2 ring-black" : ""}`}
                  style={{ borderColor: "var(--wofy-border)" }}
                  data-testid={`theme-${t.slug}`}>
                  <span className="w-4 h-4 rounded-full" style={{ background: t.primary }} />
                  <span className="w-4 h-4 rounded-full" style={{ background: t.accent }} />
                  <span className="capitalize">{t.slug}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-display text-xl mb-2">Layout</h2>
            <div className="flex flex-wrap gap-2">
              {(catalogue?.layouts || []).map(l => (
                <button key={l.slug} type="button" onClick={() => setLayout(l.slug)}
                  className={`text-sm px-3 py-1.5 rounded-full border ${layout === l.slug ? "ring-2 ring-black" : ""}`}
                  style={{ borderColor: "var(--wofy-border)" }}
                  data-testid={`layout-${l.slug}`}>
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          <div className="pt-2 flex justify-between">
            <button type="button" onClick={() => setStep(1)} className="text-sm opacity-70 hover:opacity-100 inline-flex items-center gap-1">
              <ChevronLeft className="w-4 h-4" />Back
            </button>
            <button onClick={() => setStep(3)} className="wofy-btn-primary inline-flex items-center gap-1.5" data-testid="next-step-2">
              Continue <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      )}

      {step === 3 && (
        <section className="space-y-4" data-testid="step-review">
          <div className="wofy-card !p-4">
            <div className="overline mb-1">SUMMARY</div>
            <ul className="text-sm opacity-90 space-y-1">
              <li><b>Kind:</b> {sourceType === "blank" ? "Blank site" : sourceType?.replace("_", " ")}</li>
              {sourceType !== "blank" && <li><b>Source ID:</b> <code className="font-mono text-xs">{sourceId}</code></li>}
              {sourceType === "blank" && <li><b>Name:</b> {blankName}</li>}
              <li><b>Template:</b> {catalogue?.templates?.find(t => t.slug === template)?.label}</li>
              <li><b>Theme:</b> {theme}</li>
              <li><b>Layout:</b> {catalogue?.layouts?.find(l => l.slug === layout)?.label}</li>
            </ul>
          </div>
          <p className="text-xs opacity-70">
            After creating, you'll land on the edit page where you can tweak content, add photos/videos, wire the contact form and publish.
          </p>
          <div className="flex justify-between pt-2">
            <button type="button" onClick={() => setStep(2)} className="text-sm opacity-70 hover:opacity-100 inline-flex items-center gap-1">
              <ChevronLeft className="w-4 h-4" />Back
            </button>
            <button onClick={submit} disabled={saving}
              className="wofy-btn-primary inline-flex items-center gap-1.5 disabled:opacity-40"
              data-testid="wizard-create-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Create draft
            </button>
          </div>
        </section>
      )}

      <div className="mt-8 text-xs opacity-60">
        <Globe className="w-3 h-3 inline mr-1" />Prefer to manage existing sites? <Link to="/my/websites" className="wofy-link">My websites →</Link>
      </div>
    </div>
  );
}
